import type { GraphQLResult } from 'aws-amplify/api'
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { uniqueBy } from 'remeda'
import { ReviewStatus } from '@skop-app/lib-types'

import type { ReferenceInput } from '@/types'
import type { ListReferenceProperty } from './reference.type'
import { getAmplifyClient } from '@/services/amplifyClient'
import { useUserContext } from '@/context'
import { useOrganizationMetadata } from '@/services/queries/team'
import { listReferencePropertiesQry } from '@/services/graphql/reference'
import { referenceKeys, type ReferenceListFilters } from './reference.keys'
import { referenceQueryPreset } from '@/services/query-presets'
import { ORG_METADATA_HIDE_DEFAULT_REFS_KEY } from './reference.metadata'
import { resolveInheritedOrganizationIds } from '@/utils/organization'
import { readMetadataBoolean } from '@/utils/organizationMetadata'

const client = getAmplifyClient()

const buildListReferencesSelect =
  (hideDefaultRefs: boolean) => (data: { items: ReferenceInput[] }) => {
    const items = data.items
    if (!hideDefaultRefs) return items
    return items.filter(
      (item) => item.organizationID !== process.env.NEXT_PUBLIC_SKOP_ORGID,
    )
  }

const buildListReferencesQueryOptions = ({
  queryFilters,
  hideDefaultRefs,
}: {
  queryFilters: ReferenceListFilters
  hideDefaultRefs: boolean
}) => ({
  queryKey: referenceKeys.list(queryFilters),
  queryFn: () => listReferenceProperties(queryFilters),
  ...referenceQueryPreset,
  staleTime: 1000 * 30,
  refetchOnWindowFocus: 'always' as const,
  refetchOnMount: true,
  refetchOnReconnect: true,
  select: buildListReferencesSelect(hideDefaultRefs),
})

export const useListReferences = (options?: { enabled?: boolean }) => {
  const { ownership, customSettings, isLoadingTeam, organizationScope } =
    useUserContext()
  const { data: metadata = [], isLoading: isLoadingMetadata } =
    useOrganizationMetadata(organizationScope.selectedOrganizationId, [
      ORG_METADATA_HIDE_DEFAULT_REFS_KEY,
    ])
  const hideDefaultRefs = readMetadataBoolean(
    metadata,
    ORG_METADATA_HIDE_DEFAULT_REFS_KEY,
  )

  const propertyId = customSettings?.propertyId
  const isCustomProperty =
    propertyId !== process.env.NEXT_PUBLIC_DEFAULT_PROPERTY
  const selectedOrganizationParent =
    organizationScope.selectedOrganizationParent

  const inheritedOrganizationIds = useMemo(
    () =>
      resolveInheritedOrganizationIds({
        scopeKind: organizationScope.scopeKind,
        activeOrganizationId: organizationScope.activeOrganizationId,
        activeOrganizationMasterId:
          organizationScope.activeOrganizationMasterId,
        selectedOrganizationParent,
      }),
    [
      organizationScope.scopeKind,
      organizationScope.activeOrganizationId,
      organizationScope.activeOrganizationMasterId,
      selectedOrganizationParent,
    ],
  )

  const queryFilters = useMemo(
    () => ({
      byUserID: ownership?.byUserID,
      organizationIds: [
        organizationScope.activeOrganizationId,
        ...inheritedOrganizationIds,
      ].filter((organizationId): organizationId is string =>
        Boolean(organizationId),
      ),
      isCustomProperty,
      hideDefaultRefs,
    }),
    [
      ownership?.byUserID,
      organizationScope.activeOrganizationId,
      inheritedOrganizationIds,
      isCustomProperty,
      hideDefaultRefs,
    ],
  )

  const canFetchOwnership = Boolean(
    queryFilters.byUserID || queryFilters.organizationIds.length > 0,
  )

  const enabled =
    (options?.enabled ?? true) &&
    canFetchOwnership &&
    !isLoadingTeam &&
    !isLoadingMetadata

  return useQuery({
    ...buildListReferencesQueryOptions({
      queryFilters,
      hideDefaultRefs,
    }),
    enabled,
    placeholderData: keepPreviousData,
  })
}

export const useListReferencesLoader = () => {
  const queryClient = useQueryClient()
  const { ownership, customSettings, isLoadingTeam, organizationScope } =
    useUserContext()
  const { data: metadata = [], isLoading: isLoadingMetadata } =
    useOrganizationMetadata(organizationScope.selectedOrganizationId, [
      ORG_METADATA_HIDE_DEFAULT_REFS_KEY,
    ])
  const hideDefaultRefs = readMetadataBoolean(
    metadata,
    ORG_METADATA_HIDE_DEFAULT_REFS_KEY,
  )

  const propertyId = customSettings?.propertyId
  const isCustomProperty =
    propertyId !== process.env.NEXT_PUBLIC_DEFAULT_PROPERTY
  const selectedOrganizationParent =
    organizationScope.selectedOrganizationParent

  const inheritedOrganizationIds = useMemo(
    () =>
      resolveInheritedOrganizationIds({
        scopeKind: organizationScope.scopeKind,
        activeOrganizationId: organizationScope.activeOrganizationId,
        activeOrganizationMasterId:
          organizationScope.activeOrganizationMasterId,
        selectedOrganizationParent,
      }),
    [
      organizationScope.scopeKind,
      organizationScope.activeOrganizationId,
      organizationScope.activeOrganizationMasterId,
      selectedOrganizationParent,
    ],
  )

  const queryFilters = useMemo(
    () => ({
      byUserID: ownership?.byUserID,
      organizationIds: [
        organizationScope.activeOrganizationId,
        ...inheritedOrganizationIds,
      ].filter((organizationId): organizationId is string =>
        Boolean(organizationId),
      ),
      isCustomProperty,
      hideDefaultRefs,
    }),
    [
      ownership?.byUserID,
      organizationScope.activeOrganizationId,
      inheritedOrganizationIds,
      isCustomProperty,
      hideDefaultRefs,
    ],
  )

  const canFetchOwnership = Boolean(
    queryFilters.byUserID || queryFilters.organizationIds.length > 0,
  )
  const isReady = canFetchOwnership && !isLoadingTeam && !isLoadingMetadata

  const loadReferences = useCallback(
    async (options?: {
      fresh?: boolean
    }): Promise<ReferenceInput[] | undefined> => {
      if (!isReady) {
        return undefined
      }

      const data = await queryClient.fetchQuery({
        queryKey: referenceKeys.list(queryFilters),
        queryFn: () => listReferenceProperties(queryFilters),
        ...referenceQueryPreset,
        ...(options?.fresh ? { staleTime: 0 } : {}),
      })

      return buildListReferencesSelect(hideDefaultRefs)(data)
    },
    [hideDefaultRefs, isReady, queryClient, queryFilters],
  )

  return {
    isReady,
    loadReferences,
  }
}

/**
 * Fetches raw reference properties with pagination, excluding empty ones.
 *
 * @param {Object} params - The parameters for the query.
 * @param {string | undefined} params.byUserID - The user ID to filter by.
 * @param {string[]} params.organizationIds - The readable organization IDs to query by.
 * @returns {Promise<{ items: ReferenceInput[] }>} Deduplicated raw reference items.
 */
const listReferenceProperties = async ({
  byUserID,
  organizationIds,
  isCustomProperty,
}: {
  byUserID: string | undefined
  organizationIds: readonly string[]
  isCustomProperty?: boolean
  hideDefaultRefs?: boolean
}): Promise<{ items: ReferenceInput[] }> => {
  // Build a single paginated list query with an OR filter to cover all sources
  const orFilters: Array<Record<string, unknown>> = []
  const skopOrganizationId = process.env.NEXT_PUBLIC_SKOP_ORGID
  const nonSkopOrganizationIds = organizationIds.filter(
    (organizationId) => organizationId !== skopOrganizationId,
  )
  const includesSkopOrganization = Boolean(
    skopOrganizationId && organizationIds.includes(skopOrganizationId),
  )

  if (nonSkopOrganizationIds.length > 0) {
    nonSkopOrganizationIds.forEach((organizationId) => {
      orFilters.push({
        organizationID: { eq: organizationId },
        status: { ne: ReviewStatus.REJECTED },
      })
    })
    if (!isCustomProperty && skopOrganizationId) {
      orFilters.push({
        organizationID: { eq: skopOrganizationId },
        status: { eq: ReviewStatus.ACCEPTED },
      })
    }
  } else if (includesSkopOrganization && skopOrganizationId) {
    orFilters.push({
      organizationID: { eq: skopOrganizationId },
      status: { ne: ReviewStatus.REJECTED },
    })
  } else {
    // Default references (Skop) + user-only references (no organizationID)
    orFilters.push({
      organizationID: { eq: skopOrganizationId },
      status: { eq: ReviewStatus.ACCEPTED },
    })
    orFilters.push({
      byUserID: { eq: byUserID },
      organizationID: { attributeExists: false },
    })
  }

  // Fetch all pages using nextToken
  let allItems: ReferenceInput[] = []
  let nextToken: string | null = null

  do {
    const { data } = (await client.graphql({
      query: listReferencePropertiesQry,
      variables: {
        filter: {
          or: orFilters,
        },
        limit: 10000,
        nextToken,
      },
    })) as GraphQLResult<ListReferenceProperty>

    const items = data?.listReferenceProperties?.items || []
    allItems = [...allItems, ...items]
    nextToken = data?.listReferenceProperties?.nextToken || null
  } while (nextToken)

  const items = uniqueBy(allItems, (item) => item.id)
  return { items }
}
